import React from 'react';
import styled from '@2pulse/responsive';
import { extractApiGenericError, normalize } from '@app/helpers';
import { OrderCard } from '@app/features/Service';
import { fiscalApi } from '@app/store';
import { useAppSelector } from '@app/store/hooks';

// COMMANDES D'ENTRAÎNEMENT (mode école, chantier 9c-B). Elles sont exclues de
// toutes les autres lectures — service en cours, recherche, historique client —
// pour ne jamais gonfler le chiffre d'affaires. Ce panneau est donc le seul
// endroit du dashboard où on les voit, et le seul chemin vers le retrait du mode
// école offert par la fiche de commande.
export const FiscalEcoleOrders = () => {
  // Sans boutique sélectionnée, la gateway n'a pas d'en-tête `x-shop` et répond
  // « Bad Request Exception » : on ne demande rien (retour du 9 sept.).
  const boutique = useAppSelector((state) => state.app.selectedShop);
  const { data: commandes, isFetching, isError, error } = fiscalApi.useReadFiscalEcoleOrdersQuery(undefined, { skip: !boutique });
  const liste = commandes ?? [];

  // LECTURE EN ÉCHEC (revue finale 9c-B, I6). Sans ce cas, un 403 (permission
  // `fiscal.manage` retirée) ou une gateway coupée rendait « Commandes
  // d'entraînement (0) » : le compte d'une liste qu'on n'a jamais reçue, lu
  // comme « il n'y en a aucune ». Le titre lui-même est donc remplacé — un
  // message sous un « (0) » aurait laissé le chiffre faux à l'écran.
  if (isError) {
    return (
      <Bloc testID="commandes-ecole">
        <EnTete>
          <Pastille>École</Pastille>
          <Titre>Commandes d’entraînement</Titre>
        </EnTete>
        <Alerte testID="commandes-ecole-erreur">
          Impossible de charger les commandes d’entraînement : {extractApiGenericError(error as never) ?? 'erreur réseau'}.
        </Alerte>
      </Bloc>
    );
  }

  return (
    <Bloc testID="commandes-ecole">
      <EnTete>
        <Pastille>École</Pastille>
        <Titre>Commandes d’entraînement <Compteur>({liste.length})</Compteur></Titre>
      </EnTete>
      <Rappel>Ces commandes ne sont comptées dans aucun service.</Rappel>

      {!commandes && isFetching && <Rappel>Chargement…</Rappel>}

      {commandes && liste.length === 0 && (
        <Rappel>Aucune commande d’entraînement : les ventes faites en mode école apparaissent ici tant que leur mode école n’a pas été retiré.</Rappel>
      )}

      {liste.length > 0 && (
        <Fiches>
          {liste.map((order) => (
            <OrderCard key={order._id} order={order} />
          ))}
        </Fiches>
      )}
    </Bloc>
  );
};

// Même carte que les fiches de commande qu'elle contient (`OrderCardContainer`) :
// fond translucide, coins très arrondis, pas de bordure jaune étirée.
const Bloc = styled.View(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(42),
  padding: normalize(21),
  marginBottom: normalize(21),
  rowGap: normalize(13),
}));

const EnTete = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: normalize(13),
});

const Pastille = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.oxfordblue,
  backgroundColor: '#FFC727',
  borderWidth: 1,
  borderColor: theme.colors.gold,
  borderRadius: normalize(10),
  paddingHorizontal: normalize(12),
  paddingVertical: normalize(4),
  overflow: 'hidden',
}));

const Titre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.babypowder,
}));

const Compteur = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.gold,
}));

const Rappel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.grey,
}));

const Alerte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.crimson,
}));

const Fiches = styled.View({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: normalize(13),
});
